/* eslint-disable import/no-dynamic-require, global-require */
import merge from 'lodash/merge';
import { AppDomain } from '@pegasus/config';
import { EnvironmentConfig, Config, ConfigPatch } from 'src/config/types';
import { APPDOMAIN_NAMES } from './appdomain';
import { Environment } from './environment';

type DefaultConfigurer = (environment: Environment) => Config;
type ConfigPatcher = (config: Config) => ConfigPatch;

const loadPatch = (boundRequire: any): ConfigPatcher => {
    const module = boundRequire();
    return module ? module.default : () => ({});
};

const loadDefault = (boundRequire: any): DefaultConfigurer => {
    const module = boundRequire();
    return module.default;
};

export const fetchEnvironment = () => {
    if (process.env.CONFIG_ENV == null) {
        // avoid constant substitution
        const { env } = process;
        env.CONFIG_ENV = '';
    }

    const environment = (process.env.CONFIG_ENV ||
        process.env.NODE_ENV ||
        'development') as Environment;

    return environment;
};

export const loadEnvironmentConfig = (): EnvironmentConfig => {
    const environment = fetchEnvironment();

    // load base config (development)
    const config: Config = loadDefault(() => require('./default/base'))(environment);

    // load base environment config
    merge(config, loadPatch(() => require(`./${process.env.CONFIG_ENV}/base`))(config));

    return config;
};

/* eslint-disable global-require, import/no-dynamic-require */
export const loadConfig = (appdomainId: AppDomain | undefined = undefined): Config => {
    const config = loadEnvironmentConfig() as Config;

    const clientPatchConfig: ConfigPatch =
        typeof window !== 'undefined' && window.enable ? { enable: window.enable } : {};

    // add appdomain to config
    if (appdomainId) {
        config.appdomain = APPDOMAIN_NAMES[appdomainId];
        config.appdomainId = appdomainId;
    }

    // load base appdomain config (development)
    if (appdomainId) {
        merge(config, loadPatch(() => require(`./default/default`))(config));
        merge(config, loadPatch(() => require(`./default/${config.appdomain}`))(config));
    }

    // load environment appdomain config
    if (appdomainId) {
        merge(config, loadPatch(() => require(`./${process.env.CONFIG_ENV}/default`))(config));
        merge(
            config,
            loadPatch(() => require(`./${process.env.CONFIG_ENV}/${config.appdomain}`))(config),
        );
    }

    merge(config, clientPatchConfig);

    return config;
};

export const environment = fetchEnvironment();

export const baseConfig = loadEnvironmentConfig();
